import React, { createContext, useContext, useState, useEffect } from 'react';

interface AccessibilityContextType {
  highContrast: boolean;
  textSizeMode: 'small' | 'medium' | 'large';
  skipToMainContent: () => void;
  toggleHighContrast: () => void;
  setTextSizeMode: (size: 'small' | 'medium' | 'large') => void;
}

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

export const AccessibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [highContrast, setHighContrast] = useState(false);
  const [textSizeMode, setTextSizeMode] = useState<'small' | 'medium' | 'large'>('medium');

  // Apply high contrast mode to body
  useEffect(() => {
    if (highContrast) {
      document.body.classList.add('high-contrast-mode');
    } else {
      document.body.classList.remove('high-contrast-mode');
    }
    
    return () => {
      document.body.classList.remove('high-contrast-mode');
    };
  }, [highContrast]);

  // Apply text size mode to body
  useEffect(() => {
    document.body.classList.remove('text-small', 'text-medium', 'text-large');
    document.body.classList.add(`text-${textSizeMode}`);
  }, [textSizeMode]);

  const skipToMainContent = () => {
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.focus();
      mainContent.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleHighContrast = () => {
    setHighContrast(!highContrast);
  };

  const value = {
    highContrast,
    textSizeMode,
    skipToMainContent,
    toggleHighContrast,
    setTextSizeMode
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (context === undefined) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};